import React from 'react';
import styled, { css } from 'styled-components';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { compose, withPropsOnChange, withHandlers, withState } from 'recompose';
import { RenderIfConnected } from '../../common/organisms';

const enchance = compose(
  withState('rows', 'setRows', []),
  withState('tabIndex', 'setTabIndex', 0),
  withState('sortOrder', 'setSortOrder', false),
  withPropsOnChange(['data'], (props) => {
    const { data, columns, tabIndex, onRowClick, hiddenColumns, isSort = true } = props;

    removeHiddenColumns(columns, hiddenColumns);
    const rows = isSort
      ? sortData(createRows(data, columns, onRowClick), tabIndex)
      : createRows(data, columns, onRowClick);

    return {
      props,
      rows,
    };
  }),
  withHandlers({
    sort: ({ rows, sortOrder, tabIndex, setSortOrder, setRows, setTabIndex }) => (value) => {
      const order = value === tabIndex ? !sortOrder : sortOrder;
      const sortRows = sortData(rows, value, order);

      setTabIndex(value);
      setSortOrder(order);
      setRows(sortRows);
    },
  })
);

const TableView = ({ columns, rows, sort, sortOrder, tabIndex, isMultiSelectPanel, modify }) => (
  <RenderIfConnected notEmpty={[rows]} noText>
    <TableStyled modify={modify}>
      <thead>
        <tr>
          {columns.map(({ header, sortable, style }, i) => (
            <TH key={i} onClick={() => sortable && sort(i)} sortable={sortable} sortOrder={sortOrder} style={style}>
              {header}
              {tabIndex === i && sortable && <SortIndicator sortOrder={sortOrder} />}
            </TH>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <TR key={i} onClick={row.onClick} isMultiSelect={row.isMultiSelect} isMultiSelectPanel={isMultiSelectPanel}>
            {row.isMultiSelect}
            {row.map((cell, i) => (
              <TD key={i} style={cell.style}>
                <Name>{cell.render}</Name>
              </TD>
            ))}
          </TR>
        ))}
      </tbody>
    </TableStyled>
  </RenderIfConnected>
);

export const Table = enchance(TableView);

Table.defaultProps = {
  onRowClick: () => {},
};

const sortData = (data, tabIndex, order) => {
  data.sort((a, b) => {
    const first = String(a[tabIndex].data);
    const second = String(b[tabIndex].data);

    if (order) {
      return second.localeCompare(first);
    } else {
      return first.localeCompare(second);
    }
  });

  return data;
};

const createRows = (data, columns, onRowClick) => {
  const rows = [];

  for (let [i, el] of data.entries()) {
    rows[i] = [];

    if (onRowClick) {
      rows[i].onClick = (e) => {
        if (e.currentTarget === e.target.closest('tr')) {
          onRowClick(el);
        }
      };
    }

    if (typeof el.isMultiSelect !== 'undefined') {
      rows[i].isMultiSelect = el.isMultiSelect;
    }

    for (let j of columns) {
      let render;
      const data = typeof el[j.accessor] === 'undefined' ? '' : el[j.accessor];

      if (typeof j.render === 'function') {
        render = j.render(el);
      } else {
        render = data;
      }

      rows[i].push({
        render: render,
        style: j.style,
        data: data,
      });
    }
  }

  return rows;
};

const removeHiddenColumns = (columns, toRemove) => {
  if (!toRemove) return;

  toRemove.forEach((item) => {
    const index = columns.findIndex((i) => i.accessor === item);

    if (index !== -1) {
      columns.splice(index, 1);
    }
  });

  return columns;
};

const Name = styled.div`
  padding-top: 1px;
`;

const TableStyled = styled.table`
  border-spacing: 0 2px;
  width: 100%;
  table-layout: fixed;
  border-collapse: initial;

  tbody tr {
    cursor: ${(p) => (p.modify === 'folder' ? 'pointer' : 'default')};
  }

  tbody td {
    vertical-align: ${({ modify }) => (modify === 'alignTop' ? 'top' : 'middle')};

    ${({ modify }) =>
      modify === 'alignTop' &&
      `
      height: auto;
      padding: 13px 14px;
    `}
  }

  &:not(:last-child) {
    margin-bottom: 24px;
  }
`;

const TD = styled.td`
  font-size: 14px;
  font-weight: 300;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  background: ${Colors.white};
  height: 48px;
  position: relative;
  padding: 10px 14px;
  vertical-align: middle;

  &:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  &:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  &:not(:last-child):after {
    content: '';
    position: absolute;
    right: 1px;
    top: 50%;
    height: calc(100% - 14px);
    transform: translateY(-50%);
    width: 1px;
    background-color: ${Colors.solitude};
  }
`;

const TR = styled.tr`
  ${Mixins.transition('box-shadow, opacity')};
  border-radius: 10px;
  box-shadow: ${({ isMultiSelect }) =>
    isMultiSelect ? '0 0 34px 6px rgba(97, 108, 121, 0.13), -1px 1px 2px 0 rgba(97, 108, 121, 0.2);' : 'none'};
  opacity: ${({ isMultiSelectPanel }) => (isMultiSelectPanel ? '0.5' : '1')};
  pointer-events: ${({ isMultiSelectPanel }) => (isMultiSelectPanel ? 'none' : 'all')};

  td {
    background-color: ${({ isMultiSelect }) => (isMultiSelect ? Colors.azure : Colors.white)};
  }

  &:hover td {
    ${Mixins.transition('background-color')};
    background-color: ${Colors.azure};
  }

  &:hover {
    box-shadow: 0 0 34px 6px rgba(97, 108, 121, 0.13), -1px 1px 2px 0 rgba(97, 108, 121, 0.2);
  }
`;

const TH = styled(TD)`
  font-weight: 500;
  line-height: normal;
  background: none;
  color: ${Colors.nobel};
  height: 36px;
  white-space: nowrap;
  max-width: ${(p) => p.style && p.style.width};
  user-select: none;
  ${(p) =>
    p.sortable &&
    css`
      cursor: pointer;
    `}

  &:not(:last-child):after {
    display: none;
  }
`;

const SortIndicator = styled.div`
  ${Mixins.transition('transform')};
  margin-left: 5px;
  display: inline-block;
  width: 12px;
  height: 10px;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij4gICAgPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4gICAgICAgIDxwYXRoIGZpbGw9IiNBQkFCQUIiIGZpbGwtcnVsZT0ibm9uemVybyIgZD0iTTE2LjU5IDguNTlMMTIgMTMuMTcgNy40MSA4LjU5IDYgMTBsNiA2IDYtNnoiLz4gICAgICAgIDxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiLz4gICAgPC9nPjwvc3ZnPg==);
  background-size: 15px 15px;
  background-repeat: no-repeat;
  background-position: center center;
  transform: rotate(${(p) => (p.sortOrder ? '180deg' : '0deg')});
  transform-origin: 50% 50%;
`;
